import React from 'react';
import { useFormikContext } from 'formik';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { FormValues } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';

import PlusIcon from 'assets/plus_green.svg';
import FormSection from 'components/FormSection';
import SectionWrapper from 'components/SectionWrapper';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import DetailsSection from './components/DetailsSection';
import ApplicatorsSection from './components/ApplicatorsSection';
import TagsSection from './components/TagsSection';
import NotesSection from './components/NotesSection';
import './styles.css';

/**
 * ApplicationDetailsStep component renders the application details form section.
 *
 * FormSection props:
 * - title: Section header text displayed above the form
 * - bodyHeight: Fixed height for the form container (optional, defaults to 'auto')
 * - autoHeight: When true, uses minHeight instead of fixed height for expandable content (optional, defaults to false)
 * - isOptional: Shows "(optional)" text next to title when true (optional, defaults to false)
 * - button: Custom button component displayed at the right of the title (optional)
 * - hooks can be created to store button logic per section
 *
 * SectionWrapper:
 * - Manages proper spacing between sections (32px gap)
 * - Use isFirst prop for the first section to avoid negative margin
 */
const ApplicationDetailsStep: React.FC = () => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const { operationType } = useAgOperationUtility();

  const handleAddApplicator = () => {
    setFieldValue('applicators', [
      ...values.applicators,
      { personId: '', companyId: '' }
    ]);
  };

  return (
    <div className="flex flex-col w-full pb-4 operation-details-step">
      <SectionWrapper isFirst>
        <FormSection
          title={LayoutStrings.headers[operationType!].info}
          autoHeight
        >
          <DetailsSection />
        </FormSection>
      </SectionWrapper>

      <SectionWrapper>
        <FormSection
          title={LayoutStrings.headers.applicators}
          isOptional
          button={
            <Button
              icon={<img alt="plus" src={PlusIcon} />}
              ghost
              type={ButtonType.primary}
              className="add-applicator-button"
              onClick={handleAddApplicator}
            >
              {LayoutStrings.buttons.addApplicator}
            </Button>
          }
        >
          <ApplicatorsSection />
        </FormSection>
      </SectionWrapper>

      <SectionWrapper>
        <FormSection title={LayoutStrings.headers.tags} autoHeight isOptional>
          <TagsSection />
        </FormSection>
      </SectionWrapper>

      <SectionWrapper>
        <FormSection
          title={LayoutStrings.headers.notes}
          bodyHeight={248}
          isOptional
          hasMarginBottom={false}
        >
          <NotesSection />
        </FormSection>
      </SectionWrapper>
    </div>
  );
};

export default ApplicationDetailsStep;
